import { useCallback, useState, useEffect } from 'react';
import { MapComponent } from './components/Map';
import { FilterPanel } from './components/FilterPanel';
import { ParcelPopup } from './components/ParcelPopup';
import { RecentParcels } from './components/RecentParcels';
import { useFilters } from './hooks/useFilters';
import { useParcels } from './hooks/useParcels';
import { useRecentParcels } from './hooks/useRecentParcels';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import type { MapBounds } from './types/filters';

// Simple hash-based routing
function useHashRoute(): string {
  const [route, setRoute] = useState(window.location.hash.slice(1) || '/');

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(window.location.hash.slice(1) || '/');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return route;
}

export function App() {
  const route = useHashRoute();

  // Render admin dashboard for /admin route
  if (route === '/admin' || route.startsWith('/admin')) {
    return <AdminDashboard />;
  }

  // Default: render map view
  const [flyToTarget, setFlyToTarget] = useState<{ lat: number; lng: number } | null>(null);

  const {
    filters,
    setVoivodeship,
    setTenderType,
    setPriceRange,
    resetFilters,
  } = useFilters();

  const {
    parcels,
    selectedParcel,
    loading,
    error,
    loadParcels,
    selectParcel,
  } = useParcels(filters);

  const {
    parcels: recentParcels,
    loading: recentLoading,
    error: recentError,
  } = useRecentParcels(10);

  const handleBoundsChange = useCallback(
    (bounds: MapBounds) => {
      loadParcels(bounds);
    },
    [loadParcels]
  );

  const handleParcelClick = useCallback(
    (id: string | null) => {
      selectParcel(id);
    },
    [selectParcel]
  );

  const handlePopupClose = useCallback(() => {
    selectParcel(null);
  }, [selectParcel]);

  const handleRecentParcelClick = useCallback(
    (id: string, lat: number, lng: number) => {
      setFlyToTarget({ lat, lng });
      selectParcel(id);
    },
    [selectParcel]
  );

  return (
    <div className="app">
      <header className="app-header">
        <h1>Monitor Przetargów Gminnych</h1>
        <p>Zachodniopomorskie i Lubuskie</p>
        <nav className="app-nav">
          <a href="#/admin" className="admin-link">Panel Administracyjny</a>
        </nav>
      </header>

      <main className="app-main">
        <aside className="app-sidebar">
          <FilterPanel
            filters={filters}
            onVoivodeshipChange={setVoivodeship}
            onTenderTypeChange={setTenderType}
            onPriceRangeChange={setPriceRange}
            onReset={resetFilters}
          />

          {selectedParcel && (
            <ParcelPopup parcel={selectedParcel} onClose={handlePopupClose} />
          )}

          <RecentParcels
            parcels={recentParcels}
            loading={recentLoading}
            error={recentError}
            onParcelClick={handleRecentParcelClick}
          />
        </aside>

        <div className="app-map">
          <MapComponent
            parcels={parcels}
            selectedParcel={selectedParcel}
            loading={loading}
            error={error}
            flyTo={flyToTarget}
            onBoundsChange={handleBoundsChange}
            onParcelClick={handleParcelClick}
          />
        </div>
      </main>
    </div>
  );
}
